import {
  createActionsHook as rssCreateActionsHook,
  createStateHook,
  type HookStateFunction,
  type Store,
} from 'react-sweet-state';
import pick from 'lodash/pick';

export const createActionsHook = rssCreateActionsHook;

export interface CommonContainerProps<T> {
  state?: Partial<T>;
}

interface StateContainer {
  getState: () => any;
  setState: (state: any) => void;
}

type StateUpdater<T> = T | ((currentState: T) => T);

// Fallback to string[] when TState has no TS definitions (keyof TState is never)
// Example: StateFields<{ a: string, b: number }> = ('a' | 'b')[]
// Example: StateFields<unknown> = string[] (fallback for untyped stores)
type StateFields<TState> = [keyof TState] extends [never] ? string[] : (keyof TState)[];

// Return type: Pick for typed stores, Record<field, unknown> for untyped stores
// Example typed: TState = { a: string, b: number }, fields = ['a'] -> { a: string }
// Example untyped: TState = unknown, fields = ['a'] -> { a: unknown }
type SelectorResult<TState, TFields extends readonly PropertyKey[]> = [keyof TState] extends [never]
  ? Record<TFields[number] & string, unknown>
  : Pick<TState, TFields[number] & keyof TState>;

export const createStateSelectorHook = <
  TState = any,
  TFields extends StateFields<TState> = StateFields<TState>,
>(
  Store: Store<TState, any>,
  stateFields: [...TFields]
): HookStateFunction<SelectorResult<TState, TFields>> =>
  createStateHook(Store, {
    selector: (state: TState) => pick(state, stateFields) as SelectorResult<TState, TFields>,
  });

export const createFieldSelectorHook = <TState = any>(
  Store: Store<TState, any>,
  stateField: keyof TState
): HookStateFunction<TState[keyof TState]> =>
  createStateHook(Store, {
    selector: (state: TState) => state[stateField],
  });

export const applyContainerState = <TState = any>(
  { getState, setState }: StateContainer,
  state: StateUpdater<TState> | null | undefined
): void => {
  if (!state) {
    return;
  }

  if (typeof state === 'function') {
    setState((state as (currentState: TState) => TState)(getState()));
  } else {
    setState(state);
  }
};
